import { Slot } from '@radix-ui/react-slot'
import { Children, forwardRef, isValidElement } from 'react'
import { tv, type VariantProps } from 'tailwind-variants'

const badgeRecipe = tv({
  base: ['inline-flex items-center font-normal select-none whitespace-nowrap', '[&_svg]:shrink-0'],
  variants: {
    variant: {
      solid: '',
      subtle: 'border',
      outline: 'border',
      ghost: 'border-transparent bg-transparent',
    },
    size: {
      sm: 'h-5 px-1.5 gap-1 text-xs [&_svg]:w-3 [&_svg]:h-3',
      md: 'h-6 px-2 gap-1.5 text-xs [&_svg]:w-4 [&_svg]:h-4',
    },
    shape: {
      pill: 'rounded-full',
      rounded: 'rounded-md',
    },
    colorPalette: {
      info: '',
      primary: '',
      success: '',
      warning: '',
      error: '',
    },
  },
  compoundVariants: [
    // Solid variants
    { variant: 'solid', colorPalette: 'info', class: 'bg-zinc-700 text-white' },
    { variant: 'solid', colorPalette: 'primary', class: 'bg-sky-600 text-white' },
    { variant: 'solid', colorPalette: 'success', class: 'bg-green-600 text-white' },
    { variant: 'solid', colorPalette: 'warning', class: 'bg-amber-500 text-white' },
    { variant: 'solid', colorPalette: 'error', class: 'bg-red-600 text-white' },

    // Subtle variants
    {
      variant: 'subtle',
      colorPalette: 'info',
      class: 'bg-zinc-100 border-muted/40 text-secondary-foreground [&_svg]:text-muted-foreground',
    },
    {
      variant: 'subtle',
      colorPalette: 'primary',
      class: 'bg-sky-100 border-sky-200/40 text-primary [&_svg]:text-secondary-foreground',
    },
    {
      variant: 'subtle',
      colorPalette: 'success',
      class: 'bg-green-100 border-green-200/40 text-green-700 [&_svg]:text-green-600',
    },
    {
      variant: 'subtle',
      colorPalette: 'warning',
      class: 'bg-amber-100 border-amber-200/40 text-warning [&_svg]:text-warning',
    },
    {
      variant: 'subtle',
      colorPalette: 'error',
      class: 'bg-red-100 border-red-200/40 text-red-700 [&_svg]:text-red-600',
    },

    // Outline variants
    { variant: 'outline', colorPalette: 'info', class: 'bg-zinc-50 border-border text-secondary-foreground' },
    { variant: 'outline', colorPalette: 'primary', class: 'bg-sky-50 border-sky-300 text-primary' },
    { variant: 'outline', colorPalette: 'success', class: 'bg-green-50 border-green-300 text-green-700' },
    { variant: 'outline', colorPalette: 'warning', class: 'bg-amber-50 border-amber-300 text-amber-700' },
    { variant: 'outline', colorPalette: 'error', class: 'bg-red-50 border-red-300 text-red-700' },

    // Shape rounded - reduced padding
    { shape: 'rounded', size: 'sm', class: 'px-1' },
    { shape: 'rounded', size: 'md', class: 'px-1.5' },
  ],
  defaultVariants: {
    variant: 'solid',
    size: 'md',
    shape: 'pill',
    colorPalette: 'info',
  },
})

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeRecipe> {
  asChild?: boolean
}

export const Badge = forwardRef<HTMLDivElement, BadgeProps>((props, ref) => {
  const { variant, size, shape, colorPalette, className, children, asChild, ...rest } = props

  const canUseAsChild = () => {
    if (!asChild) return false
    try {
      const child = Children.only(children)
      return isValidElement(child)
    } catch {
      return false
    }
  }

  const Comp = canUseAsChild() ? Slot : 'div'

  return (
    <Comp ref={ref} className={badgeRecipe({ variant, size, shape, colorPalette, className })} {...rest}>
      {children}
    </Comp>
  )
})

Badge.displayName = 'Badge'
